'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  DEFAULT_THEME,
  THEME_CHOICES,
  applyThemeChoice,
  readStoredChoice,
  writeStoredChoice,
  type ThemeChoice,
} from './theme-choice.ts';
import { watchSystemThemeChange } from './theme-transition.ts';

const LABELS: Record<ThemeChoice, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

const ICONS: Record<ThemeChoice, string> = {
  light: 'ph ph-sun',
  dark: 'ph ph-moon',
  system: 'ph ph-circle-half',
};

/**
 * The stored theme is an EXTERNAL mutable store, not derived state — so it is
 * read with useSyncExternalStore rather than copied into useState by an effect.
 *
 * That matters for three reasons: the server has no localStorage and needs a
 * defined snapshot to render, reading during render avoids the extra render an
 * effect causes, and subscribing to `storage` makes a change in one tab reach
 * every other tab for free.
 */
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // `storage` fires only in OTHER tabs, which is exactly the case the local
  // listener set does not cover.
  globalThis.addEventListener?.('storage', onChange);
  return () => {
    listeners.delete(onChange);
    globalThis.removeEventListener?.('storage', onChange);
  };
}

export interface ThemeToggleProps {
  /** Per-portal key, so the back office and the partner portals remember separately. */
  storageKey: string;
  className?: string;
}

export function ThemeToggle({ storageKey, className }: ThemeToggleProps) {
  const choice = useSyncExternalStore(
    subscribe,
    () => readStoredChoice(storageKey),
    // Server snapshot. The pre-paint script has already put the right theme on
    // screen; the CONTROL renders as `system` until hydration, which is the
    // honest default and never a hydration mismatch.
    () => DEFAULT_THEME,
  );

  /**
   * The OS-driven theme change has no click to hang the suppression on.
   *
   * `system` is the DEFAULT choice, so the commonest way a theme changes at all
   * is the one no handler of ours sees: the media query re-evaluates and every
   * transitioned element cross-fades through the band. The watcher is mounted
   * HERE because the house rule puts a theme toggle in every shell, which makes
   * this the one component guaranteed to exist wherever a theme can change. It
   * is not conditional on the current choice — a user on `light` can still flip
   * their OS, and the stylesheet's media query is what decides, not this state.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    return watchSystemThemeChange(document.documentElement);
  }, []);

  const select = useCallback(
    (next: ThemeChoice) => {
      writeStoredChoice(storageKey, next);
      applyThemeChoice(next, document.documentElement);
      notify();
    },
    [storageKey],
  );

  return (
    <fieldset className={className ? `seg theme-toggle ${className}` : 'seg theme-toggle'}>
      <legend className="sr-only">Colour theme</legend>
      {THEME_CHOICES.map((c) => (
        <label key={c} className="seg-opt" title={LABELS[c]}>
          <input
            type="radio"
            name={`theme-${storageKey}`}
            value={c}
            checked={choice === c}
            onChange={() => select(c)}
          />
          <i className={ICONS[c]} aria-hidden="true" />
          <span className="sr-only">{LABELS[c]}</span>
        </label>
      ))}
    </fieldset>
  );
}
